import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SignOut, CalendarDots, Clock, User, Envelope, Phone, CheckCircle, XCircle, HourglassHigh } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/demos/beauty-salon/admin');
      return;
    }
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
    } catch (error) {
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (appointmentId, newStatus) => {
    try {
      await axios.patch(`${API}/appointments/${appointmentId}/status`, { status: newStatus });
      toast.success(`Appointment ${newStatus}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update appointment');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const filteredAppointments = appointments.filter(appt => {
    if (filter === 'all') return true;
    return appt.status === filter;
  });

  const stats = {
    total: appointments.length,
    pending: appointments.filter(a => a.status === 'pending').length,
    approved: appointments.filter(a => a.status === 'approved').length,
    rejected: appointments.filter(a => a.status === 'rejected').length
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle size={18} weight="fill" className="text-green-600" />;
      case 'rejected':
        return <XCircle size={18} weight="fill" className="text-destructive" />;
      default:
        return <HourglassHigh size={18} weight="fill" className="text-accent" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      approved: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return `px-3 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="bg-background-paper border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-serif font-semibold">Admin Dashboard</h1>
          <Button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            variant="outline"
            className="gap-2"
          >
            <SignOut size={20} weight="light" />
            Logout
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-background-paper rounded-md border border-border p-6">
            <div className="text-foreground-secondary text-sm mb-1">Total Appointments</div>
            <div className="text-3xl font-serif font-medium">{stats.total}</div>
          </div>
          <div className="bg-background-paper rounded-md border border-border p-6">
            <div className="text-foreground-secondary text-sm mb-1">Pending</div>
            <div className="text-3xl font-serif font-medium text-accent">{stats.pending}</div>
          </div>
          <div className="bg-background-paper rounded-md border border-border p-6">
            <div className="text-foreground-secondary text-sm mb-1">Approved</div>
            <div className="text-3xl font-serif font-medium text-green-600">{stats.approved}</div>
          </div>
          <div className="bg-background-paper rounded-md border border-border p-6">
            <div className="text-foreground-secondary text-sm mb-1">Rejected</div>
            <div className="text-3xl font-serif font-medium text-destructive">{stats.rejected}</div>
          </div>
        </div>

        <div className="bg-background-paper rounded-md border border-border">
          <div className="p-6 border-b border-border">
            <h2 className="text-xl font-serif font-medium mb-4">Appointments</h2>
            <div className="flex gap-2">
              {['all', 'pending', 'approved', 'rejected'].map((f) => (
                <button
                  key={f}
                  data-testid={`filter-${f}-btn`}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    filter === f
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center text-foreground-secondary">Loading appointments...</div>
            ) : filteredAppointments.length === 0 ? (
              <div className="p-12 text-center text-foreground-secondary">No appointments found</div>
            ) : (
              <table className="w-full">
                <thead className="bg-background-subtle text-sm">
                  <tr>
                    <th className="text-left p-4 font-medium">Customer</th>
                    <th className="text-left p-4 font-medium">Service</th>
                    <th className="text-left p-4 font-medium">Date & Time</th>
                    <th className="text-left p-4 font-medium">Amount</th>
                    <th className="text-left p-4 font-medium">Payment</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAppointments.map((appt) => (
                    <tr key={appt.id} className="border-t border-border hover:bg-background-subtle transition-colors">
                      <td className="p-4">
                        <div className="font-medium">{appt.customer_name}</div>
                        <div className="text-sm text-foreground-secondary flex items-center gap-1 mt-1">
                          <Envelope size={14} weight="light" />
                          {appt.customer_email}
                        </div>
                        <div className="text-sm text-foreground-secondary flex items-center gap-1 mt-1">
                          <Phone size={14} weight="light" />
                          {appt.customer_phone}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{appt.service_name}</div>
                        {appt.notes && (
                          <div className="text-sm text-foreground-secondary mt-1">{appt.notes}</div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          <CalendarDots size={16} weight="light" className="text-foreground-secondary" />
                          <span>{appt.appointment_date}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <Clock size={16} weight="light" className="text-foreground-secondary" />
                          <span>{appt.appointment_time}</span>
                        </div>
                      </td>
                      <td className="p-4 font-medium">${appt.amount.toFixed(2)}</td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          appt.payment_status === 'paid' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {appt.payment_status}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(appt.status)}
                          <span className={getStatusBadge(appt.status)}>{appt.status}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {appt.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              data-testid={`approve-appt-${appt.id}-btn`}
                              size="sm"
                              onClick={() => handleStatusUpdate(appt.id, 'approved')}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              Approve
                            </Button>
                            <Button
                              data-testid={`reject-appt-${appt.id}-btn`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusUpdate(appt.id, 'rejected')}
                              className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;